import React, { useEffect, useState } from "react";
import baseURL from "../../authComponents/baseURL/BaseURL";
import { BiSearch } from "react-icons/bi";
import { FaFileDownload } from "react-icons/fa";
import * as XLSX from "xlsx";

const Countries = () => {
  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        const response = await baseURL.get("/get_all_countries");
        if (response.data && response.data.countries) {
          setCountries(response.data.countries);
          setFilteredCountries(response.data.countries);
        } else {
          console.error("Unexpected API response format:", response.data);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    if (searchTerm === "") {
      setFilteredCountries(countries);
    } else {
      const filtered = countries.filter(
        (country) =>
          country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          country.full_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCountries(filtered);
    }
    setCurrentPage(1);
  }, [searchTerm, countries]);

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredCountries.slice(
    indexOfFirstEntry,
    indexOfLastEntry
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const totalPages = Math.ceil(filteredCountries.length / entriesPerPage);

  // Function to download countries data as Excel
  const downloadExcel = () => {
    setDownloadLoading(true);
    try {
      // Prepare data for Excel
      const excelData = filteredCountries.map((country, index) => ({
        ID: index + 1,
        Name: country.name,
        "Full Name": country.full_name,
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Countries");

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      // Create Blob and download
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create download link and trigger click
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "countries_data.xlsx";
      link.click();

      window.URL.revokeObjectURL(url);
      setDownloadLoading(false);
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      setDownloadLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold text-gray-700 font-serif">
          Countries
        </h1>

        <button
          onClick={downloadExcel}
          disabled={
            downloadLoading || loading || filteredCountries.length === 0
          }
          className="flex items-center bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded shadow transition-colors disabled:bg-gray-400"
        >
          {downloadLoading ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
              <span>Exporting...</span>
            </>
          ) : (
            <>
              <FaFileDownload className="mr-2" />
              <span>Download Countries Data</span>
            </>
          )}
        </button>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <div className="flex items-center mb-4 md:mb-0">
          <span className="mr-2">Show</span>
          <select
            className="border rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-[#730000]"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="ml-2">entries</span>
        </div>

        <div className="relative">
          <span className="mr-2">Search:</span>
          <input
            type="text"
            className="border rounded px-3 py-1 w-64 focus:outline-none focus:ring-2 focus:ring-[#730000]"
            placeholder="Search countries..."
            value={searchTerm}
            onChange={handleSearch}
          />
          <BiSearch className="absolute right-3 top-2 text-gray-400" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#730000] mx-auto"></div>
          <p className="mt-2">Loading...</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    Full Name
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentEntries.map((country, index) => (
                  <tr key={country.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {country.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {country.full_name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center mt-4">
            <p className="text-sm text-gray-600">
              Showing {indexOfFirstEntry + 1} to{" "}
              {Math.min(indexOfLastEntry, filteredCountries.length)} of{" "}
              {filteredCountries.length} entries
            </p>

            <div className="flex">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-l hover:bg-gray-100 disabled:opacity-50"
              >
                Previous
              </button>

              {totalPages <= 7 ? (
                [...Array(totalPages)].map((_, index) => (
                  <button
                    key={index}
                    onClick={() => paginate(index + 1)}
                    className={`px-3 py-1 border-t border-b border-r ${
                      currentPage === index + 1
                        ? "bg-[#730000] text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))
              ) : (
                <>
                  {[1, 2, 3].map((number) => (
                    <button
                      key={number}
                      onClick={() => paginate(number)}
                      className={`px-3 py-1 border-t border-b border-r ${
                        currentPage === number
                          ? "bg-[#730000] text-white"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      {number}
                    </button>
                  ))}

                  <button className="px-3 py-1 border-t border-b border-r">
                    ...
                  </button>

                  {[totalPages - 2, totalPages - 1, totalPages].map(
                    (number) => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`px-3 py-1 border-t border-b border-r ${
                          currentPage === number
                            ? "bg-[#730000] text-white"
                            : "hover:bg-gray-100"
                        }`}
                      >
                        {number}
                      </button>
                    )
                  )}
                </>
              )}

              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border-t border-b border-r rounded-r hover:bg-gray-100 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Countries;
